/**
 * Actycrea: resize.esm.js (ES6)
 * MIT
 *
 * Global resize/breakpoint watcher. Dispatches on `window` the three custom
 * events other behaviours already listen for in main.js — `onResizeInit`,
 * `onResizeEnd` and `breakpointChange` — none of which were emitted
 * anywhere before this module (dead listeners waiting for a source).
 *
 * - `onResizeInit`     fires on the first tick of a resize burst.
 * - `onResizeEnd`      fires once resizing settles (debounced).
 * - `breakpointChange` fires only when the active breakpoint actually
 *   changes, not on every resize tick.
 *
 * Breakpoints mirror `$grid-breakpoints` in
 * src/scss/config/_variables.scss — keep both in sync.
 *
 * @module esm/resize
 */

const BREAKPOINTS = {
  xs: 0 ,
  sm: 576 ,
  md: 768 ,
  lg: 992 ,
  xl: 1200 ,
  xxl: 1512
};

const DEBOUNCE_DELAY = 200; // ms — settle time before onResizeEnd fires

let lastBreakpoint = null;
let debounceId = null;
let resizing = false;
let started = false;

/**
 * Resolves the active breakpoint name for the current viewport width.
 *
 * @returns {string} One of the keys of BREAKPOINTS.
 */
function currentBreakpoint( ){
  const width = window.innerWidth;
  let active = 'xs';

  Object.keys( BREAKPOINTS ).forEach( ( name ) => {
    if( width >= BREAKPOINTS[ name ] ) active = name;
  } );

  return active;
}

/**
 * Handles a single native resize tick: fires `onResizeInit` once per burst,
 * then debounces `onResizeEnd` and, if it changed, `breakpointChange`.
 *
 * @returns {void}
 */
function onResize( ){
  if( !resizing ){
    resizing = true;
    window.dispatchEvent( new CustomEvent( 'onResizeInit' , {
      detail: { width: window.innerWidth , height: window.innerHeight }
    } ) );
  }

  clearTimeout( debounceId );
  debounceId = setTimeout( ( ) => {
    resizing = false;

    const breakpoint = currentBreakpoint( );
    window.dispatchEvent( new CustomEvent( 'onResizeEnd' , {
      detail: { width: window.innerWidth , height: window.innerHeight , breakpoint }
    } ) );

    if( breakpoint !== lastBreakpoint ){
      const previous = lastBreakpoint;
      lastBreakpoint = breakpoint;
      window.dispatchEvent( new CustomEvent( 'breakpointChange' , {
        detail: { breakpoint , previous }
      } ) );
    }
  } , DEBOUNCE_DELAY );
}

/**
 * Starts the global resize watcher. Safe to call more than once — only
 * binds the native listener the first time.
 *
 * @returns {void}
 */
export function initResizeWatcher( ){
  if( started ) return;
  started = true;

  lastBreakpoint = currentBreakpoint( );
  window.addEventListener( 'resize' , onResize , { passive: true } );
}

export default initResizeWatcher;
